import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/DashboardBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, PlayCircle, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

export default function CoursesPage() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/courses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title={t("training_courses")} subtitle={`${list.length} courses`}
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-course-button"><Plus className="h-4 w-4 mr-1" /> {t("add_course")}</Button></DialogTrigger>
            <Form onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <Card key={c.id} className="border border-slate-200" data-testid={`course-${c.id}`}>
            <CardContent className="p-5">
              <PlayCircle className="h-8 w-8 text-blue-600 mb-2" />
              <div className="font-heading text-lg font-semibold text-slate-950">{c.title}</div>
              <p className="text-sm text-slate-600 mt-1">{c.description}</p>
              <div className="flex items-center justify-between mt-4">
                {c.video_url ? <a href={c.video_url} target="_blank" rel="noreferrer" className="text-blue-600 text-sm hover:underline">Open Video</a> : <span/>}
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={async () => {
                  if (!window.confirm("Delete course?")) return;
                  try { await api.delete(`/courses/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
                }} data-testid={`course-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {list.length === 0 && <p className="text-slate-500">No courses added yet.</p>}
      </div>
    </div>
  );
}

function Form({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", video_url: "", duration_min: 30, is_free: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/courses", f); toast.success("Course added"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle className="font-heading">Add Course</DialogTitle><DialogDescription>Publish training content for field executives.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="course-title" /></div>
        <div><Label>Description</Label><Textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="course-desc" /></div>
        <div><Label>Video URL (YouTube/etc.)</Label><Input value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} data-testid="course-video" /></div>
        <div><Label>Duration (minutes)</Label><Input type="number" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: Number(e.target.value) })} data-testid="course-duration" /></div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="course-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
